/**
 * Banner Library Component
 *
 * A gallery view of all uploaded banner images that can be reused.
 * Shows image previews, usage counts, and allows selection for new banners.
 *
 * @module BannerManagement
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Card,
    CardMedia,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Pagination,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Search as SearchIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    CheckCircle as CheckCircleIcon,
    CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';
import authInstance from '../../services/httpClient';

interface BannerLibraryItem {
    imageKey: string;
    imageUrl: string;
    firstUsed: string;
    usageCount: number;
    items: Array<{ id: string; name: string }>;
}

interface BannerLibraryProps {
    open: boolean;
    onClose: () => void;
    onSelect: (imageUrl: string, imageKey?: string) => void;
    selectedImageUrl?: string | null;
}

const ITEMS_PER_PAGE = 12;

/**
 * Banner Library Dialog
 *
 * Displays a gallery of all previously uploaded banner images.
 * Allows users to select an existing image or switch to upload mode.
 */
const BannerLibrary: React.FC<BannerLibraryProps> = ({
    open,
    onClose,
    onSelect,
    selectedImageUrl,
}) => {
    const notify = useNotify();
    const [library, setLibrary] = useState<BannerLibraryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const listTopRef = useScrollToListTop(page);
    const [showUploadMode, setShowUploadMode] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);

    // Fetch library on open
    useEffect(() => {
        if (open) {
            fetchLibrary();
        }
    }, [open]);

    const fetchLibrary = async () => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await authInstance.get('/admin/banner-items/library');
            setLibrary(data.data || []);
        } catch (err: any) {
            setError(err?.message || 'Failed to load banner library');
            notify('Failed to load banner library', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (imageKey: string, usageCount: number) => {
        if (usageCount > 0) {
            notify('Cannot delete: Image is still in use by banner items', { type: 'warning' });
            return;
        }

        if (!window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
            return;
        }

        setDeleting(imageKey);

        try {
            await authInstance.delete(`/admin/banner-items/library/${encodeURIComponent(imageKey)}`);

            notify('Image deleted successfully', { type: 'success' });
            fetchLibrary();
        } catch (err: any) {
            notify(err?.message || 'Failed to delete image', { type: 'error' });
        } finally {
            setDeleting(null);
        }
    };

    const handleSelect = (imageUrl: string, imageKey?: string) => {
        onSelect(imageUrl, imageKey);
        onClose();
    };

    // Filter library based on search
    const filteredLibrary = library.filter((item) =>
        item.items.some((i) => i.name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Pagination
    const totalPages = Math.ceil(filteredLibrary.length / ITEMS_PER_PAGE);
    const paginatedLibrary = filteredLibrary.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh', maxHeight: '90vh' },
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon color="primary" />
                        <Typography variant="h6">Banner Library</Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showUploadMode}
                                onChange={(e) => setShowUploadMode(e.target.checked)}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CloudUploadIcon fontSize="small" />
                                <Typography variant="body2">Upload New</Typography>
                            </Box>
                        }
                    />
                </Box>
            </DialogTitle>

            <DialogContent>
                {showUploadMode ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            Switch back to library view to select existing images, or close this dialog and use the upload option in the banner form.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setShowUploadMode(false)}
                            startIcon={<ImageIcon />}
                        >
                            Back to Library
                        </Button>
                    </Box>
                ) : (
                    <>
                        {/* Search Bar */}
                        <Box ref={listTopRef} sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                placeholder="Search by banner name..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                size="small"
                            />
                        </Box>

                        {/* Error State */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Loading State */}
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {/* Empty State */}
                        {!loading && library.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No Images in Library
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Upload banner images to see them here. Once uploaded, you can reuse them across multiple banners.
                                </Typography>
                            </Box>
                        )}

                        {/* Empty Search Results */}
                        {!loading && library.length > 0 && filteredLibrary.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    No images match your search
                                </Typography>
                            </Box>
                        )}

                        {/* Image Grid */}
                        {!loading && paginatedLibrary.length > 0 && (
                            <>
                                <Grid container spacing={2}>
                                    {paginatedLibrary.map((item) => {
                                        const isSelected = selectedImageUrl === item.imageUrl;
                                        const isInUse = item.usageCount > 0;

                                        return (
                                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.imageKey}>
                                                <Card
                                                    sx={{
                                                        position: 'relative',
                                                        cursor: 'pointer',
                                                        border: isSelected ? 2 : 1,
                                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                                        '&:hover': {
                                                            borderColor: 'primary.main',
                                                        },
                                                    }}
                                                    onClick={() => handleSelect(item.imageUrl, item.imageKey)}
                                                >
                                                    {/* Selected Indicator */}
                                                    {isSelected && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                zIndex: 1,
                                                                bgcolor: 'primary.main',
                                                                borderRadius: '50%',
                                                                p: 0.5,
                                                            }}
                                                        >
                                                            <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                                                        </Box>
                                                    )}

                                                    {/* Delete Button */}
                                                    <Tooltip title={isInUse ? 'Cannot delete: Image is in use' : 'Delete image'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    left: 8,
                                                                    zIndex: 1,
                                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                                    '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(item.imageKey, item.usageCount);
                                                                }}
                                                                disabled={isInUse || deleting === item.imageKey}
                                                            >
                                                                {deleting === item.imageKey ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <DeleteIcon fontSize="small" color={isInUse ? 'disabled' : 'error'} />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    {/* Image Preview */}
                                                    <CardMedia
                                                        component="img"
                                                        image={item.imageUrl}
                                                        alt="Banner preview"
                                                        sx={{
                                                            height: 140,
                                                            objectFit: 'cover',
                                                        }}
                                                    />

                                                    {/* Info Footer */}
                                                    <Box sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                            First used: {new Date(item.firstUsed).toLocaleDateString()}
                                                        </Typography>
                                                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                                                            <Chip
                                                                size="small"
                                                                label={`${item.usageCount} use${item.usageCount !== 1 ? 's' : ''}`}
                                                                color={isInUse ? 'primary' : 'default'}
                                                                variant={isInUse ? 'filled' : 'outlined'}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={(_, value) => setPage(value)}
                                            color="primary"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BannerLibrary;
